import { moneyShort, money, pct } from '../lib/format';

export interface BridgeStep {
  label: string;
  /** Signed contribution. `total` steps restate the running figure instead. */
  value: number;
  kind: 'start' | 'add' | 'subtract' | 'total';
  note?: string;
}

export interface BridgeSegment {
  title: string;
  caption?: string;
  steps: BridgeStep[];
}

/**
 * The valuation bridge.
 *
 * A valuation report's most-asked question is "how did you get from the
 * property value to the figure on the IHT400?" — and the answer is a chain
 * of deductions, each of which is separately arguable. Drawing it as a
 * waterfall puts the size of each argument in front of the reviewer:
 * a latent-tax bar taller than the marketability bar is a valuation whose
 * defence rests on the latent-tax position.
 *
 * Every bar traces to a calculation step; nothing here is computed in the
 * browser beyond laying out numbers the engine produced.
 */
export function ValuationBridge({ segments }: { segments: BridgeSegment[] }) {
  return (
    <div className="space-y-8">
      {segments.map((segment) => (
        <Waterfall key={segment.title} segment={segment} />
      ))}
    </div>
  );
}

const H = 210;
const TOP = 26;
const BOTTOM = 34;

function Waterfall({ segment }: { segment: BridgeSegment }) {
  const steps = segment.steps;
  if (steps.length === 0) return null;

  // Walk the steps to find each bar's floor and ceiling.
  const bars: { step: BridgeStep; from: number; to: number }[] = [];
  let running = 0;
  for (const step of steps) {
    if (step.kind === 'start') {
      bars.push({ step, from: 0, to: step.value });
      running = step.value;
    } else if (step.kind === 'total') {
      bars.push({ step, from: 0, to: step.value });
      running = step.value;
    } else {
      const delta = step.kind === 'subtract' ? -Math.abs(step.value) : Math.abs(step.value);
      bars.push({ step, from: running, to: running + delta });
      running += delta;
    }
  }

  const max = Math.max(...bars.flatMap((b) => [b.from, b.to]), 0);
  const scale = max > 0 ? (H - TOP - BOTTOM) / max : 0;
  const y = (v: number) => H - BOTTOM - v * scale;

  const colWidth = 100 / bars.length;
  const barWidth = Math.min(colWidth * 0.56, 11);

  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <h4 className="text-sm font-semibold text-slate-800">{segment.title}</h4>
        {segment.caption ? <p className="text-xs text-slate-400">{segment.caption}</p> : null}
      </div>

      <div className="relative w-full overflow-x-auto">
        <svg
          viewBox={`0 0 100 ${H}`}
          preserveAspectRatio="none"
          className="h-[210px] w-full min-w-[520px]"
          role="img"
          aria-label={`${segment.title} waterfall`}
        >
          {/* baseline */}
          <line
            x1={0}
            x2={100}
            y1={H - BOTTOM}
            y2={H - BOTTOM}
            stroke="#e2e8f0"
            strokeWidth={0.3}
            vectorEffect="non-scaling-stroke"
          />

          {bars.map((bar, i) => {
            const cx = colWidth * i + colWidth / 2;
            const top = Math.min(y(bar.from), y(bar.to));
            const height = Math.max(Math.abs(y(bar.to) - y(bar.from)), 1.5);
            const fill =
              bar.step.kind === 'total'
                ? '#0f172a'
                : bar.step.kind === 'start'
                  ? '#0d9488'
                  : bar.step.kind === 'subtract'
                    ? '#e11d48'
                    : '#059669';

            const prev = bars[i - 1];
            return (
              <g key={`${bar.step.label}-${i}`}>
                {prev ? (
                  <line
                    x1={colWidth * (i - 1) + colWidth / 2 + barWidth / 2}
                    x2={cx - barWidth / 2}
                    y1={y(prev.to)}
                    y2={y(prev.to)}
                    stroke="#cbd5e1"
                    strokeWidth={0.25}
                    strokeDasharray="1 1"
                    vectorEffect="non-scaling-stroke"
                  />
                ) : null}
                <rect
                  x={cx - barWidth / 2}
                  y={top}
                  width={barWidth}
                  height={height}
                  rx={0.6}
                  fill={fill}
                  opacity={bar.step.kind === 'subtract' ? 0.85 : 1}
                >
                  <title>{`${bar.step.label}: ${money(bar.step.value)}`}</title>
                </rect>
              </g>
            );
          })}
        </svg>

        {/* Labels sit in HTML rather than SVG text: the viewBox is stretched
            non-uniformly to fill the width, which would distort glyphs. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 flex min-w-[520px]">
          {bars.map((bar, i) => (
            <div key={`v-${i}`} className="flex-1 px-0.5 text-center">
              <span
                className={`tnum text-[11px] font-semibold ${
                  bar.step.kind === 'subtract'
                    ? 'text-rose-600'
                    : bar.step.kind === 'total'
                      ? 'text-slate-900'
                      : bar.step.kind === 'add'
                        ? 'text-emerald-600'
                        : 'text-teal-700'
                }`}
              >
                {bar.step.kind === 'subtract' ? '−' : bar.step.kind === 'add' ? '+' : ''}
                {moneyShort(Math.abs(bar.step.value))}
              </span>
            </div>
          ))}
        </div>

        <div className="absolute inset-x-0 bottom-0 flex min-w-[520px]">
          {bars.map((bar, i) => (
            <div key={`l-${i}`} className="flex-1 px-1 text-center">
              <span
                className={`block text-[10px] leading-tight ${
                  bar.step.kind === 'total' ? 'font-semibold text-slate-700' : 'text-slate-500'
                }`}
              >
                {bar.step.label}
              </span>
              {bar.step.note ? (
                <span className="block text-[10px] leading-tight text-slate-400">
                  {bar.step.note}
                </span>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Builds the bridge segments from calculation outputs. */
export function buildSegments(outputs: {
  nav?: Record<string, string>;
  capitalised?: Record<string, string>;
  bridge?: Record<string, string>;
  holding?: Record<string, string>;
}): BridgeSegment[] {
  const segments: BridgeSegment[] = [];
  const n = (v: string | undefined): number => (v === undefined ? 0 : Number(v));

  if (outputs.nav) {
    const nav = outputs.nav;
    segments.push({
      title: 'Company value — net asset build',
      caption: 'Every bar traces to a calculation step',
      steps: [
        { label: 'Gross assets', value: n(nav.gross_assets), kind: 'start' },
        { label: 'Liabilities', value: n(nav.total_liabilities), kind: 'subtract' },
        ...(n(nav.latent_tax_deduction) > 0
          ? [
              {
                label: 'Latent tax',
                value: n(nav.latent_tax_deduction),
                kind: 'subtract' as const,
              },
            ]
          : []),
        { label: 'Net asset value', value: n(nav.net_asset_value), kind: 'total' },
      ],
    });
  } else if (outputs.bridge) {
    const b = outputs.bridge;
    segments.push({
      title: 'Enterprise value to equity',
      steps: [
        { label: 'Enterprise value', value: n(b.enterprise_value), kind: 'start' },
        { label: 'Net debt', value: n(b.net_debt), kind: 'subtract' },
        ...(n(b.debt_like_total) > 0
          ? [{ label: 'Debt-like items', value: n(b.debt_like_total), kind: 'subtract' as const }]
          : []),
        ...(n(b.surplus_total) > 0
          ? [{ label: 'Surplus assets', value: n(b.surplus_total), kind: 'add' as const }]
          : []),
        ...(n(b.working_capital_adjustment) !== 0
          ? [
              {
                label: 'Working capital',
                value: Math.abs(n(b.working_capital_adjustment)),
                kind: (n(b.working_capital_adjustment) < 0 ? 'subtract' : 'add') as
                  | 'subtract'
                  | 'add',
              },
            ]
          : []),
        { label: 'Equity value', value: n(b.equity_value), kind: 'total' },
      ],
    });
  }

  if (outputs.holding) {
    const h = outputs.holding;
    const proRata = n(h.pro_rata_value);
    const final = n(h.holding_value);
    const discount = proRata - final;
    segments.push({
      title: 'The holding — pro-rata to market value',
      caption: h.aggregated_pct
        ? `Discounts set by reference to the aggregated ${pct(h.aggregated_pct, 1)} interest (IHTA s.161)`
        : undefined,
      steps: [
        {
          label: 'Pro-rata share',
          value: proRata,
          kind: 'start',
          note: h.pct_holding ? pct(h.pct_holding, 1) : undefined,
        },
        {
          label: 'Discounts',
          value: discount,
          kind: 'subtract',
          note: h.effective_discount_pct ? pct(h.effective_discount_pct, 1) : undefined,
        },
        { label: 'Market value of the holding', value: final, kind: 'total' },
      ],
    });
  }

  return segments;
}
